import React from 'react';
import { Box, ColumnLayout, Container, Link, SpaceBetween } from '@amzn/awsui-components-core';
import './PickUpWhereYouLeftOffContainer.scss';

export interface PickUpWhereYouLeftOffContainerProps {
  /** The provisioned WorkDocs folder URL, or null when this quarter's folder isn't set up yet. */
  workDocsFolderUrl: string | null;
  /** The current user's alias, for the Manager Hub deep link. */
  alias: string;
  /** The current user's first name, for the greeting (falls back to the alias upstream). */
  firstName: string;
}

/**
 * A returning-user greeting shown above the wizard in a bordered Cloudscape Container, styled
 * after the EA Huddle+ hero: a large "Hey {firstName}, welcome back!" heading (the container's
 * own title — no separate Header) with the first name rendered in the shared
 * gradient (see PickUpWhereYouLeftOffContainer.scss + src/styles/_gradients.scss), followed by
 * the two entry points a returning user needs — open the provisioned WorkDocs folder (view/update
 * existing cards) or jump to Manager Hub (input ratings, view team). Presentation-only — the link
 * targets are derived from props. The WorkDocs link is active whenever `workDocsFolderUrl` is
 * non-null (the page supplies either the live token-verified URL or, for a returning user without
 * a token, the folder id persisted on their card); when null it is rendered as muted text with a
 * hint, since Cloudscape's Link has no disabled state.
 */
export const PickUpWhereYouLeftOffContainer: React.FC<PickUpWhereYouLeftOffContainerProps> = ({ workDocsFolderUrl, alias, firstName }) => {
  const managerHubUrl = `https://atoz.amazon.work/manager/team/${alias}/talent-cycle/calibrate`;

  return (
    <Container>
      <div className="baseball-cards-greeting-hero">
        <SpaceBetween size="m">
          <div>
            {/* Greeting styled to match the EA Huddle+ hero (font-size / letter-spacing /
                line-height live in PickUpWhereYouLeftOffContainer.scss). A plain element rather
                than <Box variant="h1"> so the Huddle greeting spec can be applied directly —
                Cloudscape v3 deprecates className on Box, and its awsui font classes would win on
                specificity anyway. The gradient rides the inner name span. */}
            <div className="baseball-cards-greeting">
              Hey <span className="baseball-cards-greeting__name">{firstName}</span>, welcome back!
            </div>
            <Box color="text-body-secondary">
              Your baseball cards are stored in your WorkDocs folder. You can revisit them anytime or head to Manager Hub to manage ratings.
            </Box>
          </div>

          <ColumnLayout columns={2}>
            {workDocsFolderUrl ? (
              <SpaceBetween size="xxs">
                <Link href={workDocsFolderUrl} external externalIconAriaLabel="Opens in a new tab">
                  Open My WorkDocs Folder
                </Link>
                <Box color="text-body-secondary" fontSize="body-s">
                  View or Update the cards you&apos;ve already generated.
                </Box>
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxs">
                <Box color="text-status-inactive">Open My WorkDocs Folder</Box>
                <Box color="text-body-secondary" fontSize="body-s">
                  Set up your folder in the wizard below first.
                </Box>
              </SpaceBetween>
            )}

            <SpaceBetween size="xxs">
              <Link href={managerHubUrl} external externalIconAriaLabel="Opens in a new tab">
                Go to Manager Hub
              </Link>
              <Box color="text-body-secondary" fontSize="body-s">
                Enter ratings and review your team.
              </Box>
            </SpaceBetween>
          </ColumnLayout>
        </SpaceBetween>
      </div>
    </Container>
  );
};
